import { fill, identity, isEqual, isString, orderBy, slice, without } from 'lodash';
import { isEmpty, negate } from 'lodash/fp';

/**
 * Returns the type of the elements in the array.
 */
export type ArrayElement<T> = T extends (infer U)[] ? U : never;

export const times = (count: number): number[] => fill(Array(count), 0).map((_, i) => i);

export const notEmpty = <T>(value: T | null | undefined): value is T => value != null;

export const isNotEmpty = negate(isEmpty);

/**
 * Wraps the value in an array if it is defined, otherwise returns an empty array.
 */
export const toArrayOrEmpty = <T>(value: T | null | undefined): T[] => (value != null ? [value] : []);

/**
 * This will not mutate the original array. Will give a new array each time, except for the empty array.
 *
 * @param array To rearrange the items in.
 * @param fromIndex move the item at fromIndex
 * @param toIndex move the item to toIndex.
 */
export const move = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
  if (isEmpty(array)) {
    return array;
  } else {
    const newArray = [...array];
    const element = newArray.splice(fromIndex, 1)[0];
    if (element) {
      newArray.splice(toIndex, 0, element);
    }
    return newArray;
  }
};

/**
 * Slices an array at given indexes.
 *
 * Eg. toSlices([0, 1, 2, 3, 4, 5, 6, 7, 8, 9], [2, 4, 9]) => [[0, 1], [2, 3], [4, 5, 6, 7, 8], [9]]
 *
 * Disregards any sliceIndexes which are 0 or equal or more to length of the list.
 * Sorts the list of given indexes before slicing.
 *
 * Returns a new array consisting of slices done on the passed in array.
 * Returns empty array if no idxs is passed in.
 *
 * @param array to slice.
 * @param sliceIndexes Indexes to make slices from.
 */
export const toSlices = <T>(array: T[], sliceIndexes: number[]): T[][] => {
  const cleanedSliceIndexes = without(sliceIndexes, 0, array.length).filter(idx => idx < array.length);

  if (isEmpty(cleanedSliceIndexes)) {
    return [];
  } else {
    const sortedSliceIndexes = orderBy(cleanedSliceIndexes, identity, 'asc');
    const slices: T[][] = [];

    let currentSliceIndex = 0;
    for (let i = 0; i < sortedSliceIndexes.length + 1; i++) {
      const slicedArr = slice(
        array,
        currentSliceIndex,
        i <= sortedSliceIndexes.length ? sortedSliceIndexes[i] : sortedSliceIndexes.length,
      );
      slices.push(slicedArr);
      const nextIndex = sortedSliceIndexes[i];
      if (!nextIndex) {
        break;
      }
      currentSliceIndex = nextIndex;
    }

    return slices;
  }
};

/**
 * Inserts an item into an array at a given index. Returns a new array.
 *
 * @param array to insert into.
 * @param index to insert at.
 * @param item to insert.
 * @throws Error if index is less than 0 or greater than array length.
 *
 * @returns new array with item inserted.
 *
 * @example
 * insert([1, 2, 3], 1, 4) => [1, 4, 2, 3]
 * insert([1, 2, 3], 0, 4) => [4, 1, 2, 3]
 **/
export const insertAt = <T>(array: T[], item: T, index: number): T[] => {
  if (index < 0) {
    throw new Error('Index must be 0 or greater');
  } else if (index > array.length) {
    throw new Error('Index must be less than or equal to array length');
  }

  const newArray = [...array];
  newArray.splice(index, 0, item);
  return newArray;
};

export const getSoleEntryOrUndefined = <T>(array: T[]): T | undefined => (array.length === 1 ? array[0] : undefined);

export const insertBetween = <T>(betweenElement: T, elements: T[]): T[] =>
  elements.flatMap(element => [betweenElement, element]).slice(1);

export const hasSameEntries = <T>(arryA: T[], arryB: T[]): boolean => {
  const [sortedA, sortedB] = [arryA, arryB].map(arr =>
    [...arr].sort((a, b) => {
      if (isString(a) && isString(b)) {
        return a.localeCompare(b);
      }
      return Number(a) - Number(b);
    }),
  );
  return isEqual(sortedA, sortedB);
};

/**
 * Filters an array and removes from the original. Returns two arrays, one with the filtered items and one with the remaining unfiltered items.
 * @param array to filter
 * @param predicate filtering function
 * @returns two arrays with filtered and remaining items.
 * @example
 * filterAndRemove([1,2,3,4], (v) => v%2 == 0) => [[2,4], [1,3]]
 */
export const filterAndRemove = <T>(array: T[], predicate: (value: T) => unknown): [T[], T[]] => {
  const filteredArray: T[] = [];
  const leftoverArray: T[] = [];

  array.forEach(value => {
    const shouldFilter = predicate(value);
    if (shouldFilter) {
      filteredArray.push(value);
    } else {
      leftoverArray.push(value);
    }
  });
  return [filteredArray, leftoverArray];
};

type ArrayFn<T, U> = (v: T, i: number, list: T[]) => U;

/**
 * Maps an array according to the mapper function and filters out the values that are null/undefined
 * @param array to process
 * @param mapper mapping function
 * @returns a filtered and mapped array
 * @example
 * filterMap([1,2,3,4], (v) => v%2 == 0 ? v.toString() : null) => ["2", "4"]
 */
export const filterMap = <T, U>(array: T[], mapper: ArrayFn<T, U | undefined | null>): U[] =>
  array.reduce<U[]>((p, c, i, a) => {
    const value = mapper(c, i, a);
    if (value != null) {
      p.push(value);
    }
    return p;
  }, []);

/**
 * Returns true if the array contains only strings, empty array is considered to contain only strings.
 * @param array to check
 * @returns true if all elements are strings
 * @example
 * isStringArray(["a", "b"]) => true
 * isStringArray(["a", 1]) => false
 * isStringArray([]) => true
 */
export const isStringArray = (array: unknown[]): array is string[] => array.every(isString);
